/**
 * LmsSyncWorkspace — Správa tab body for `/vozidla/sprava?tab=lms-sync`.
 *
 * Lead pull + masterdata cache sync against /v1/lms/*.
 *
 * Sibling to LmsCatalogWorkspace (`?tab=lms`) — that one does the deeper
 * factory/DB diff. This one handles the daily sync chores.
 */

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { Pill } from '@/components/Pill';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { formatInt } from '@/lib/format';
import {
  lmsPobockaErrorLabel,
  useLmsMasterdataQuery,
  useLmsNewLeadsQuery,
  useLmsSyncDisinterestMutation,
  useLmsSyncVehicleModelsMutation,
  useLmsTakeOverMutation,
  type LmsMasterdataKey,
} from '@/modules/vozidla/api';

const MASTERDATA_TABS: Array<{ key: LmsMasterdataKey; label: string }> = [
  { key: 'actions', label: 'Akce' },
  { key: 'salespeople', label: 'Prodejci' },
  { key: 'lead-origins', label: 'Původy leadů' },
  { key: 'lead-sources', label: 'Zdroje leadů' },
  { key: 'vehicle-models', label: 'Vozy' },
  { key: 'disinterest-reasons', label: 'Důvody nezájmu' },
  { key: 'dealerships', label: 'Dealerství' },
];

function defaultLeadDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 10);
  return d.toISOString().slice(0, 10);
}

function isoDateToApi(value: string): string {
  return new Date(value + 'T00:00:00Z').toISOString();
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.body && typeof err.body === 'object' && typeof err.body.error === 'string') {
      return err.body.error;
    }
    return err.message || fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function LmsSyncWorkspace() {
  const navigate = useNavigate();
  const [date, setDate] = useState<string>(defaultLeadDate());
  const [datumIso, setDatumIso] = useState<string | null>(isoDateToApi(defaultLeadDate()));
  const [previewKey, setPreviewKey] = useState<LmsMasterdataKey | null>(null);

  const newLeads = useLmsNewLeadsQuery(datumIso);
  const takeOver = useLmsTakeOverMutation();
  const syncDuvody = useLmsSyncDisinterestMutation();
  const syncVozy = useLmsSyncVehicleModelsMutation();
  const masterdata = useLmsMasterdataQuery(previewKey);

  const onLoadLeads = () => {
    setDatumIso(isoDateToApi(date));
  };

  const onTakeOver = (leadId: number, pobockaId: number) => {
    takeOver.mutate({ leadId, pobockaId }, {
      onSuccess: (res) => {
        toast.ok(`Lead č. ${res.lms_id} převzat · ${res.jmeno || 'kontakt'}`);
      },
      onError: (err) => {
        toast.error(errorMessage(err, 'Nepodařilo se převzít lead.'));
      },
    });
  };

  const onSyncDuvody = () => {
    syncDuvody.mutate(undefined, {
      onSuccess: ({ inserted, total }) => {
        toast.ok(`Důvody nezájmu · přidáno ${formatInt(inserted)}, celkem ${formatInt(total)}`);
      },
      onError: (err) => toast.error(errorMessage(err, 'Synchronizace selhala.')),
    });
  };

  const onSyncVozy = () => {
    syncVozy.mutate(undefined, {
      onSuccess: ({ inserted, total }) => {
        toast.ok(`Vozy · přidáno ${formatInt(inserted)}, celkem ${formatInt(total)}`);
      },
      onError: (err) => toast.error(errorMessage(err, 'Synchronizace selhala.')),
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-row">
      <div
        data-testid="lms-sync-page"
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-6 pt-3 max-sm:px-4"
      >
        <SectionCard
          title="Nové leady"
          eyebrow="01 · LEADS"
          subtitle="Nasaj nové leady ze systému LMS od zvoleného data, převezmi je do kontaktů."
        >
          <div className="flex flex-wrap items-end gap-3 border-b border-line pb-4">
            <label className="flex flex-col gap-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                Datum (od)
              </span>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="border border-line bg-paper px-2 py-1.5 font-mono text-[12px] text-ink focus:border-accent focus:outline-none"
              />
            </label>
            <Button
              variant="ink"
              size="sm"
              icon={<Icon name="download" size={12} />}
              onClick={onLoadLeads}
              disabled={newLeads.isFetching}
            >
              {newLeads.isFetching ? 'Načítám…' : 'Načíst nové leady'}
            </Button>
          </div>
          <div className="mt-4">
            {newLeads.isError ? (
              <ErrorLine>
                {errorMessage(newLeads.error, 'Nepodařilo se načíst leady.')}
              </ErrorLine>
            ) : !newLeads.data ? (
              <NeutralLine>Vyberte datum a načtěte nové leady.</NeutralLine>
            ) : (
              <div className="flex flex-col gap-4">
                {newLeads.data.pobocky.map((p) => {
                  const error = lmsPobockaErrorLabel(p.error);
                  return (
                    <div key={p.id} data-testid="lms-sync-pobocka">
                      <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        {/* Location name only matters once there is more than one. */}
                        {newLeads.data.pobocky.length > 1 && (
                          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-ink">
                            {p.nazev}
                          </span>
                        )}
                        {!error && (
                          <span className="font-mono text-[11px] uppercase tracking-[0.05em] text-ink-3">
                            LMS · {p.lead_ids.length} celkem · {p.new_ids.length} nepřevzatých ·{' '}
                            {p.existing_ids.length} v DB
                          </span>
                        )}
                      </div>
                      {error ? (
                        <ErrorLine>{error}</ErrorLine>
                      ) : p.new_ids.length === 0 ? (
                        <NeutralLine>Žádné nové nepřevzaté leady v LMS.</NeutralLine>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {p.new_ids.map((id) => (
                            <Button
                              key={id}
                              variant="secondary"
                              size="sm"
                              icon={<Icon name="check" size={12} />}
                              onClick={() => onTakeOver(id, p.id)}
                              disabled={takeOver.isPending}
                            >
                              Převzít lead č. {id}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </SectionCard>

        <SectionCard
          title="Synchronizace masterdata"
          eyebrow="02 · CACHE"
          subtitle="Stáhni z LMS aktuální seznamy a ulož je do lokální cache (lms_duvod_nezajmu, lms_vozy)."
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <SyncRow
              label="Důvody nezájmu"
              caption="lms_duvod_nezajmu"
              onClick={onSyncDuvody}
              busy={syncDuvody.isPending}
              result={syncDuvody.data}
              error={syncDuvody.error}
            />
            <SyncRow
              label="Vozy (LMS ID)"
              caption="lms_vozy"
              onClick={onSyncVozy}
              busy={syncVozy.isPending}
              result={syncVozy.data}
              error={syncVozy.error}
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Náhled masterdata"
          eyebrow="03 · INSPECT"
          subtitle="Klepni na položku — surová data se zobrazí v pravém panelu."
        >
          <div className="flex flex-wrap gap-2">
            {MASTERDATA_TABS.map((tab) => {
              const active = previewKey === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setPreviewKey(tab.key)}
                  className={
                    'border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.05em] transition-colors ' +
                    (active
                      ? 'border-ink bg-ink text-paper'
                      : 'border-line bg-paper text-ink-2 hover:border-ink hover:text-ink')
                  }
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          {previewKey && (
            <p className="mt-3 font-mono text-[10.5px] uppercase tracking-[0.06em] text-ink-3 max-sm:break-all">
              Aktivní · {previewKey} → /v1/lms/masterdata/{previewKey}
            </p>
          )}
        </SectionCard>

        <SectionCard
          title="Porovnání s továrnou"
          eyebrow="04 · COMPARE"
          subtitle="Plné porovnání modelů a skladu je v LMS katalogu (sousední záložka)."
        >
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() =>
                navigate({ to: '/vozidla/sprava', search: { tab: 'lms' }, replace: true })
              }
              className="inline-flex items-center gap-1.5 border border-line bg-paper px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.05em] text-ink hover:border-ink"
              data-testid="lms-sync-management-link"
            >
              <Icon name="external" size={12} />
              Otevřít LMS katalog
            </button>
          </div>
        </SectionCard>
      </div>

      <aside className="hidden w-[360px] shrink-0 border-l border-line bg-paper lg:flex lg:min-h-0 lg:flex-col">
        <MasterdataInspector record={masterdata.data} loading={masterdata.isLoading} />
      </aside>
    </div>
  );
}

function SectionCard({
  eyebrow,
  title,
  subtitle,
  children,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border border-line bg-paper p-5">
      <header className="mb-4 flex flex-col gap-1 border-b border-line pb-3">
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          — {eyebrow}
        </span>
        <h2 className="font-display text-[22px] font-medium tracking-[-0.01em] text-ink">
          {title}
        </h2>
        {subtitle && <p className="text-[12.5px] text-ink-2">{subtitle}</p>}
      </header>
      {children}
    </section>
  );
}

function SyncRow({
  label,
  caption,
  onClick,
  busy,
  result,
  error,
}: {
  label: string;
  caption: string;
  onClick: () => void;
  busy: boolean;
  result?: { inserted: number; total: number };
  error?: unknown;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border border-line bg-bg/40 px-3 py-2.5">
      <div className="flex min-w-0 flex-col">
        <span className="text-[13px] font-medium tracking-[-0.01em] text-ink">{label}</span>
        <span className="font-mono text-[10.5px] uppercase tracking-[0.05em] text-ink-3">
          {caption}
        </span>
        {error ? (
          <span className="mt-1 font-mono text-[10.5px] uppercase tracking-[0.05em] text-warn">
            Chyba · {errorMessage(error, 'Sync selhal.')}
          </span>
        ) : result ? (
          <span className="mt-1 font-mono text-[10.5px] uppercase tracking-[0.05em] text-ok">
            Přidáno {formatInt(result.inserted)} · celkem {formatInt(result.total)}
          </span>
        ) : null}
      </div>
      <Button
        variant="ink"
        size="sm"
        icon={<Icon name="download" size={12} />}
        onClick={onClick}
        disabled={busy}
      >
        {busy ? 'Sync…' : 'Sync'}
      </Button>
    </div>
  );
}

function ErrorLine({ children }: { children: React.ReactNode }) {
  return (
    <div className="border border-warn bg-warn-soft px-3 py-2 font-mono text-[11px] uppercase tracking-[0.04em] text-warn">
      {children}
    </div>
  );
}

function NeutralLine({ children }: { children: React.ReactNode }) {
  return (
    <div className="border border-line bg-bg/40 px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.06em] text-ink-3">
      {children}
    </div>
  );
}

function MasterdataInspector({
  record,
  loading,
}: {
  record: { key: LmsMasterdataKey; payload: Record<string, unknown> } | undefined;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="grid h-full place-items-center px-6 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
        Načítám…
      </div>
    );
  }

  if (!record) {
    return (
      <div className="grid h-full place-items-center px-6 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
        Vyber masterdata pro náhled
      </div>
    );
  }

  const tab = MASTERDATA_TABS.find((t) => t.key === record.key);
  const firstArray = Object.values(record.payload).find(Array.isArray) as
    | Array<unknown>
    | undefined;
  const itemCount = firstArray?.length ?? 0;
  const empty = Object.keys(record.payload).length === 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="border-b border-line px-5 py-4">
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          — {record.key.toUpperCase()}
        </span>
        <h3 className="font-display text-[18px] font-medium tracking-[-0.01em] text-ink">
          {tab?.label ?? record.key}
        </h3>
        <span className="mt-1 inline-flex items-center gap-2">
          <Pill>{formatInt(itemCount)} záznamů</Pill>
        </span>
      </header>
      <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
        {empty ? (
          <div className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-ink-3">
            LMS vrátil prázdnou odpověď.
          </div>
        ) : (
          <pre className="whitespace-pre-wrap break-all border border-line bg-bg/40 p-3 font-mono text-[10.5px] leading-relaxed text-ink-2">
            {JSON.stringify(record.payload, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}
